import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { authAPI, projectAPI, threadAPI } from '../lib/api';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { formatDistanceToNow } from 'date-fns';
import { 
  FolderOpen, Plus, Edit, Trash2, Eye, MessageSquare, Loader2, MoreVertical
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

export default function MyProjects() {
  const [currentUser, setCurrentUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const userRes = await authAPI.getMe();
      setCurrentUser(userRes.data);

      const projectsRes = await projectAPI.getAll({ limit: 100 });
      setProjects(projectsRes.data.filter(p => p.author_id === userRes.data.user_id));
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (projectId) => {
    if (!confirm('Are you sure you want to delete this project?')) return;
    
    try {
      await projectAPI.delete(projectId);
      setProjects(prev => prev.filter(p => p.project_id !== projectId));
      toast.success('Project deleted');
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </div>
    );
  }

  const publishedProjects = projects.filter(p => p.status !== 'draft');
  const draftProjects = projects.filter(p => p.status === 'draft');

  return (
    <div className="min-h-screen bg-slate-50" data-testid="my-projects-page">
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-slate-900">My Projects</h1>
          <Link to="/projects/create">
            <Button className="bg-indigo-600">
              <Plus className="w-4 h-4 mr-2" />
              New Project
            </Button>
          </Link>
        </div>

        <Tabs defaultValue="published">
          <TabsList className="mb-6">
            <TabsTrigger value="published" className="gap-2">
              Published ({publishedProjects.length})
            </TabsTrigger>
            <TabsTrigger value="drafts" className="gap-2">
              Drafts ({draftProjects.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="published">
            {publishedProjects.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <FolderOpen className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">No published projects</p>
                  <Link to="/projects/create" className="mt-4 inline-block">
                    <Button className="bg-indigo-600">Create Your First Project</Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {publishedProjects.map((project) => (
                  <Card key={project.project_id} className="card-hover">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <Link to={`/projects/${project.project_id}`}>
                            <h3 className="font-semibold text-slate-900 hover:text-indigo-600 mb-1">
                              {project.title}
                            </h3>
                          </Link>
                          <p className="text-sm text-slate-600 line-clamp-2 mb-3">
                            {project.description}
                          </p>
                          
                          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <Eye className="w-4 h-4" />
                              {project.views || 0} views
                            </span>
                            <span className="flex items-center gap-1">
                              ↑ {project.upvotes - project.downvotes}
                            </span>
                            <span className="flex items-center gap-1">
                              <MessageSquare className="w-4 h-4" />
                              {project.comment_count}
                            </span>
                            <span>
                              {formatDistanceToNow(new Date(project.created_at), { addSuffix: true })}
                            </span>
                          </div>
                        </div>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <Link to={`/projects/${project.project_id}`}>
                                <Eye className="w-4 h-4 mr-2" />
                                View
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDelete(project.project_id)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="drafts">
            {draftProjects.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <FolderOpen className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">No draft projects</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {draftProjects.map((project) => (
                  <Card key={project.project_id} className="border-dashed">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-slate-900">
                              {project.title || 'Untitled Project'}
                            </h3>
                            <Badge variant="secondary">Draft</Badge>
                          </div>
                          <p className="text-sm text-slate-500">
                            Last edited {formatDistanceToNow(new Date(project.updated_at || project.created_at), { addSuffix: true })}
                          </p>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm">
                            <Edit className="w-4 h-4 mr-2" />
                            Continue Editing
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDelete(project.project_id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
